<template>
  <div class="container-fluid section-p70" v-if="formData.id">
    <div class="row align-items-center">
      <div class="col-auto title-blue">{{ t("MANAGE_INSTITUTION") }}</div>

      <div class="col">
        <Button
            :label="isInstitutionArchived ? t('ARCHIVED') : t('ACTIVE')"
            :class="isInstitutionArchived ? 'p-button-danger' : 'blue'"
            v-tooltip.bottom="isInstitutionArchived ? t('UNARCHIVE') : t('ARCHIVE')"
            @click="toggleArchive()"
        />
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-xl-4">
        <Card class="text-start">
          <template #title>{{ t("BASE_DATA") }}</template>

          <template #content>
            <form @submit="updateInstitution">
              <div class="mb-3">
                <label for="id-registry-code" class="form-label">{{ t('REGISTRATION_NUMBER') }}</label>
                <InputText
                    id="id-registry-code"
                    class="w-100"
                    maxlength="8"
                    v-model="formData.registryCode"
                    :disabled="isInstitutionArchived"
                    :invalid="validationPending && !validateRegistryCode(formData.registryCode)"
                />
              </div>

              <div class="mb-3">
                <label for="id-business-name" class="form-label">{{ t('INSTITUTION_NAME') }}</label>
                <InputText
                    id="id-business-name"
                    class="w-100"
                    v-model="formData.businessName"
                    :disabled="isInstitutionArchived"
                    :invalid="validationPending && !formData.businessName"
                />
              </div>

              <Button type="submit" :label="t('SAVE')" :disabled="isInstitutionArchived" class="w-100 blue"/>
            </form>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useI18n} from "vue-i18n";
import {computed, onMounted, ref} from "vue";
import {injectAxios} from "@/util/inject";
import type {InstitutionDto} from "@/model/institution";
import {useRoute} from "vue-router";
import {useToast} from "@/util/toastUtils";
import {validateRegistryCode} from "@/util/validationUtils";

const {t} = useI18n();
const toast = useToast();
const axios = injectAxios();
const route = useRoute();

const validationPending = ref(false);
const formData = ref<Partial<InstitutionDto>>({});

onMounted(async () => {
  await fetchInstitution();
});

const isInstitutionArchived = computed(() => {
  return formData.value?.isArchived;
});

const isUpdateFormValid = computed(() => {
  return !!formData.value && !!formData.value.businessName && validateRegistryCode(formData.value.registryCode);
});

const fetchInstitution = async () => {
  const resp = await axios.get(`/institutions/${route.params.id}`);
  formData.value = resp.data;
};

const updateInstitution = async (ev: Event) => {
  ev.preventDefault();

  if (!isUpdateFormValid.value) {
    validationPending.value = true;
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
    return;
  }

  validationPending.value = false;
  await axios.put(`/institutions/${route.params.id}`, {...formData.value});
  await fetchInstitution();
  toast.makeSuccessToast(t("SAVED"));
};

const toggleArchive = async () => {
  await axios.post(`/institutions/${route.params.id}/archive`);
  await fetchInstitution();
  toast.makeSuccessToast(t("SAVED"));
};
</script>
